import { useEffect, useState } from 'react'
import { Building2, Menu, ChevronDown, FolderTree, Package, Edit, Trash2 } from 'lucide-react'
import { useBusinessStore } from '../../core/stores/businessStore'
import { useMenuManagementStore } from '../../core/stores/menuManagementStore'
import { MenuCategory, MenuItem } from '../../core/models/menu'
import { Card, CardContent } from '../../components/ui/Card'
import AddButton from '../../components/buttons/AddButton'
import CreateCategoryModal from '../../components/menu/CreateCategoryModal'
import EditCategoryModal from '../../components/menu/EditCategoryModal'
import CreateItemModal from '../../components/menu/CreateItemModal'
import EditItemModal from '../../components/menu/EditItemModal'
import DeleteModal from '../../components/ui/DeleteModal'
import { useTranslation } from '../../core/hooks/useTranslation'
import { useAuthStore } from '../../core/stores/authStore'
import { UserRole } from '../../core/enums'

export default function MenuManagementPage() {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const isBusinessOwner = user?.userRole === UserRole.BUSINESS_OWNER
  const { businesses, fetchBusinessesByRole, isLoading, error } = useBusinessStore()
  const {
    categories,
    items,
    fetchCategories,
    fetchItemsByBusiness,
    setBusinessId,
    clearData,
    deleteCategory,
    deleteItem
  } = useMenuManagementStore()

  const [selectedBusinessId, setSelectedBusinessId] = useState<number | null>(null)
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false)
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)

  // Category modals
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<MenuCategory | null>(null)
  const [deletingCategory, setDeletingCategory] = useState<MenuCategory | null>(null)

  // Item modals
  const [showCreateItemModal, setShowCreateItemModal] = useState(false)
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null)
  const [deletingItem, setDeletingItem] = useState<MenuItem | null>(null)

  useEffect(() => {
    fetchBusinessesByRole(user?.userRole!, user?.businessId!)
  }, [fetchBusinessesByRole])

  // Auto-select business if only one exists
  useEffect(() => {
    if (!isLoading && businesses.length === 1 && !selectedBusinessId) {
      setSelectedBusinessId(businesses[0].id)
    }
  }, [businesses, isLoading, selectedBusinessId])

  useEffect(() => {
    if (selectedBusinessId) {
      setBusinessId(selectedBusinessId)
      fetchCategories(selectedBusinessId)
      fetchItemsByBusiness(selectedBusinessId)
      setSelectedCategoryId(null)
    } else {
      clearData()
      setSelectedCategoryId(null)
    }
  }, [selectedBusinessId, setBusinessId, fetchCategories, fetchItemsByBusiness, clearData])

  // Auto-select first category when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && selectedCategoryId === null) {
      const sortedCategories = [...categories].sort((a, b) => a.sortOrder - b.sortOrder)
      setSelectedCategoryId(sortedCategories[0].id)
    }
  }, [categories, selectedCategoryId])

  const selectedBusiness = businesses.find(b => b.id === selectedBusinessId)

  // Filter items by selected category and sort by sortOrder
  const filteredItems = items
    .filter(item => {
      if (selectedCategoryId && item.categoryId !== selectedCategoryId) {
        return false
      }
      return true
    })
    .sort((a, b) => a.sortOrder - b.sortOrder)

  // Category handlers
  const handleCategoryEdit = (category: MenuCategory) => {
    setEditingCategory(category)
  }

  const handleCategoryDeleteClick = (category: MenuCategory) => {
    setDeletingCategory(category)
  }

  const handleCategoryDeleteConfirm = async () => {
    if (deletingCategory) {
      try {
        await deleteCategory(deletingCategory.id)
        setDeletingCategory(null)
        if (selectedCategoryId === deletingCategory.id) {
          setSelectedCategoryId(null)
        }
      } catch (error) {
        // Error handled in store
      }
    }
  }

  // Item handlers
  const handleItemEdit = (item: MenuItem) => {
    setEditingItem(item)
  }

  const handleItemDeleteClick = (item: MenuItem) => {
    setDeletingItem(item)
  }

  const handleItemDeleteConfirm = async () => {
    if (deletingItem) {
      try {
        await deleteItem(deletingItem.id)
        setDeletingItem(null)
        await fetchItemsByBusiness(selectedBusinessId!)
      } catch (error: any) {
        if (error?.response?.status && error.response.status < 500) {
          setDeletingItem(null)
        }
      }
    }
  }

  if (isLoading && businesses.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-4">
      {/* Header with Business Selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="text-gray-700 mt-1">{t('menuManagementTitle')}</p>
        </div>

        {/* Business Dropdown - Compact */}
        {businesses.length > 0 && (
          <div className="relative w-full sm:w-auto">
            <button
              onClick={() => setShowBusinessDropdown(!showBusinessDropdown)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors bg-white w-full sm:min-w-[200px]"
            >
              {selectedBusiness ? (
                <>
                  {selectedBusiness.logoUrl ? (
                    <img
                      src={selectedBusiness.logoUrl}
                      alt={selectedBusiness.name}
                      className="w-6 h-6 rounded object-cover"
                    />
                  ) : (
                    <Building2 className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="flex-1 text-right font-medium text-gray-900 truncate">
                    {selectedBusiness.name}
                  </span>
                </>
              ) : (
                <span className="flex-1 text-right text-gray-500">{t('selectBusiness')}</span>
              )}
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${showBusinessDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showBusinessDropdown && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                {businesses.map((business) => (
                  <button
                    key={business.id}
                    onClick={() => {
                      setSelectedBusinessId(business.id)
                      setShowBusinessDropdown(false)
                    }}
                    className={`w-full flex items-center gap-2 p-2 hover:bg-gray-50 transition-colors ${selectedBusinessId === business.id ? 'bg-blue-50' : ''
                      }`}
                  >
                    {business.logoUrl ? (
                      <img
                        src={business.logoUrl}
                        alt={business.name}
                        className="w-6 h-6 rounded object-cover"
                      />
                    ) : (
                      <Building2 className="w-5 h-5 text-gray-400" />
                    )}
                    <span className="flex-1 text-right font-medium text-gray-900 truncate">
                      {business.name}
                    </span>
                    {selectedBusinessId === business.id && (
                      <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0"></div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Business Selection */}
      {businesses.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noBusinessesFound')}</h3>
            <p className="text-gray-600 text-center mb-4">
              {t('noBusinessesFoundDescription')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>

          {/* Menu Management - Split View */}
          {selectedBusinessId ? (
            <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 min-h-0 lg:h-[calc(100vh-200px)]">
              {/* Categories Sidebar - Right */}
              <div className="lg:col-span-1 flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden min-h-0 max-h-[40vh] flex-shrink-0 lg:max-h-none lg:flex-shrink">
                {/* Categories Header */}
                <div className="p-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <FolderTree className="w-4 h-4 text-orange-600" />
                        {t('categories')}
                      </h3>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {categories.length} {t('categoryCount')}
                      </p>
                    </div>
                    {isBusinessOwner && (
                      <AddButton
                        onClick={() => setShowCreateCategoryModal(true)}
                        title={t('addNewCategory')}
                        variant="icon-only"
                        className="py-2 px-2"
                      />
                    )}
                  </div>
                </div>

                {/* Categories List */}
                <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {categories.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-6 px-3">
                      <FolderTree className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-xs text-gray-600 text-center mb-2">
                        {t('noCategories')}
                      </p>
                      {isBusinessOwner && (
                        <AddButton
                          onClick={() => setShowCreateCategoryModal(true)}
                          title={t('createNewCategory')}
                          variant="icon-only"
                        />
                      )}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200 pb-2">
                      {categories
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((category) => {
                          const itemCount = items.filter(item => item.categoryId === category.id).length
                          const isSelected = selectedCategoryId === category.id
                          return (
                            <div
                              key={category.id}
                              onClick={() => setSelectedCategoryId(category.id)}
                              className={`w-full p-2.5 text-right transition-colors cursor-pointer ${isSelected
                                ? 'bg-blue-50'
                                : 'hover:bg-gray-50'
                                }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-medium text-gray-900 truncate mb-0.5">
                                    {category.name}
                                  </h4>
                                  <div className="flex items-center gap-2">

                                    <p className="text-xs text-gray-500">
                                      {itemCount} {t('itemCount')}
                                    </p>
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${category.isVisible
                                      ? ''
                                      : 'bg-red-100 text-red-700'
                                      }`}>
                                      {category.isVisible ? '' : t('inactive')}
                                    </span>

                                  </div>
                                </div>
                                {isBusinessOwner && (
                                <div className="flex items-center gap-0.5">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleCategoryEdit(category)
                                    }}
                                    className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                                    title={t('edit')}
                                    type="button"
                                  >
                                    <Edit className="w-4 h-4 text-gray-600" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleCategoryDeleteClick(category)
                                    }}
                                    className="p-1.5 hover:bg-red-100 rounded transition-colors"
                                    title={t('delete')}
                                    type="button"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  )}
                </div>
              </div>

              {/* Items Content - Left */}
              <div className="lg:col-span-2 flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden min-h-0 flex-1 max-h-[85vh] lg:max-h-none">
                {/* Items Header */}
                <div className="p-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <Package className="w-4 h-4 text-orange-600" />
                        {selectedCategoryId
                          ? categories.find(c => c.id === selectedCategoryId)?.name || t('items')
                          : t('allItems')
                        }
                      </h3>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {filteredItems.length} {t('itemCount')}
                      </p>
                    </div>
                    {isBusinessOwner && (
                    <AddButton
                      onClick={() => setShowCreateItemModal(true)}
                      title={t('addNewItem')}
                      variant="icon-only"
                      disabled={categories.length === 0}
                      className="py-2 px-2"
                    />
                    )}
                  </div>
                </div>

                {/* Items List */}
                <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {categories.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Package className="w-10 h-10 text-gray-400 mb-3" />
                      <p className="text-sm text-gray-600 text-center mb-3">
                        {t('createCategoryFirst')}
                      </p>
                      <AddButton
                        onClick={() => setShowCreateCategoryModal(true)}
                        title={t('createNewCategory')}
                      />
                    </div>
                  ) : filteredItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Package className="w-10 h-10 text-gray-400 mb-3" />
                      <p className="text-sm text-gray-600 text-center mb-3">
                        {selectedCategoryId ? t('noItemsInCategory') : t('noItems')}
                      </p>
                      {isBusinessOwner && (
                        <AddButton
                          onClick={() => setShowCreateItemModal(true)}
                          title={t('addNewItem')}
                        />
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Desktop Table View */}
                      <div className="hidden lg:block overflow-x-auto pb-2">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10 shadow-sm">
                            <tr>
                              <th className="px-2 py-2 text-right text-xs font-semibold text-gray-900">{t('image')}</th>
                              <th className="px-2 py-2 text-right text-xs font-semibold text-gray-900">{t('name')}</th>
                              <th className="px-2 py-2 text-right text-xs font-semibold text-gray-900">{t('description')}</th>
                              {isBusinessOwner && (
                                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-900">{t('actions')}</th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredItems.map((item) => (
                              <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-2 py-2">
                                  {item.imageUrl ? (
                                    <img
                                      src={item.imageUrl}
                                      alt={item.name}
                                      className="w-10 h-10 object-cover rounded"
                                    />
                                  ) : (
                                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                                      <Package className="w-5 h-5 text-gray-400" />
                                    </div>
                                  )}
                                </td>
                                <td className="px-2 py-2">
                                  <div>
                                    <div className="text-sm text-gray-800 mb-1">{item.name}</div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-bold text-orange-600">
                                        {item.price.toFixed(2)} <span className="text-xs text-gray-500">{t('currency')}</span>
                                      </span>
                                      <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium ${item.isAvailable
                                        ? ''
                                        : 'bg-red-100 text-red-700'
                                        }`}>
                                        {item.isAvailable ? '' : t('unavailable')}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-2 py-2">
                                  <div className="text-sm text-gray-600 max-w-xs break-words whitespace-normal">
                                    {item.description || '-'}
                                  </div>
                                </td>
                                {isBusinessOwner && (
                                <td className="px-2 py-2">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => handleItemEdit(item)}
                                      className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                                      title={t('edit')}
                                    >
                                      <Edit className="w-4 h-4 text-gray-700" />
                                    </button>
                                    <button
                                      onClick={() => handleItemDeleteClick(item)}
                                      className="p-1.5 hover:bg-red-100 rounded transition-colors"
                                      title={t('delete')}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-600" />
                                    </button>
                                  </div>
                                </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Card View */}
                      <div className="lg:hidden space-y-2 p-2">
                        {filteredItems.map((item) => (
                          <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm">
                            <div className="flex gap-3">
                              {/* Image */}
                              <div className="flex-shrink-0">
                                {item.imageUrl ? (
                                  <img
                                    src={item.imageUrl}
                                    alt={item.name}
                                    className="w-12 h-12 object-cover rounded"
                                  />
                                ) : (
                                  <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                                    <Package className="w-6 h-6 text-gray-400" />
                                  </div>
                                )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <h4 className="text-sm font-semibold text-gray-900 truncate">{item.name}</h4>
                                  <span className="text-base font-bold text-orange-600">
                                    {item.price.toFixed(2)} <span className="text-xs text-gray-500">{t('currency')}</span>
                                  </span>
                                </div>

                                {item.description && (
                                  <p className="text-sm text-gray-600 line-clamp-2 break-words">
                                    {item.description}
                                  </p>
                                )}

                                <hr className="my-1 border-gray-200 border-dotted" />

                                <div className='flex items-center justify-between'>
                                  <div className="flex items-center gap-1 flex-wrap">
                                    {!item.isAvailable && (
                                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                        {t('unavailable')}
                                      </span>
                                    )}
                                  </div>

                                  {isBusinessOwner && (
                                  <div className="flex items-center gap-1 flex-shrink-0 justify-end">
                                    <button
                                      onClick={() => handleItemEdit(item)}
                                      className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                                      title={t('edit')}
                                    >
                                      <Edit className="w-4 h-4 text-gray-700" />
                                    </button>
                                    <button
                                      onClick={() => handleItemDeleteClick(item)}
                                      className="p-1.5 hover:bg-red-100 rounded transition-colors"
                                      title={t('delete')}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-600" />
                                    </button>
                                  </div>
                                  )}
                                </div>

                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Menu className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-gray-600">{t('selectBusinessToManageMenu')}</p>
              </CardContent>
            </Card>
          )}

          {/* Modals */}
          <CreateCategoryModal
            businessId={selectedBusinessId!}
            isOpen={showCreateCategoryModal}
            onClose={() => setShowCreateCategoryModal(false)}
          />

          {editingCategory && (
            <EditCategoryModal
              category={editingCategory}
              isOpen={!!editingCategory}
              onClose={() => setEditingCategory(null)}
            />
          )}

          {deletingCategory && (
            <DeleteModal
              isOpen={!!deletingCategory}
              onClose={() => setDeletingCategory(null)}
              onConfirm={handleCategoryDeleteConfirm}
              title={t('deleteCategory')}
              message={`${t('confirmDelete')} ${t('category')} "${deletingCategory.name}"؟`}
            />
          )}

          <CreateItemModal
            businessId={selectedBusinessId!}
            categories={categories}
            isOpen={showCreateItemModal}
            onClose={() => setShowCreateItemModal(false)}
            onItemCreated={() => {
              if (selectedBusinessId) {
                fetchItemsByBusiness(selectedBusinessId)
              }
            }}
            defaultCategoryId={selectedCategoryId}
          />

          {editingItem && (
            <EditItemModal
              item={editingItem}
              categories={categories}
              isOpen={!!editingItem}
              onClose={() => setEditingItem(null)}
              onItemUpdated={() => {
                if (selectedBusinessId) {
                  fetchItemsByBusiness(selectedBusinessId)
                }
              }}
            />
          )}

          {deletingItem && (
            <DeleteModal
              isOpen={!!deletingItem}
              onClose={() => setDeletingItem(null)}
              onConfirm={handleItemDeleteConfirm}
              title={t('deleteItem')}
              message={`${t('confirmDelete')} ${t('item')} "${deletingItem.name}"؟`}
            />
          )}

        </>
      )}
    </div>
  )
}